import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import StudioSuiteNav from '../../components/studio/StudioSuiteNav';
import BrandProfileManager from '../../components/studio/BrandProfileManager';
import { groupedProfileOptions } from '../../components/studio/brandProfileOptions';
import {
  Brain, Search, Loader2, ExternalLink, Bookmark, Sparkles, Megaphone, Layers, Filter,
  ChevronDown, ChevronRight, ChevronLeft, X, Save, Trash2, Eye, Wand2, Send, BarChart3, FolderOpen,
  ListChecks, Plus, Lightbulb, ShieldCheck, Clock, TrendingUp, Target, Award, CheckSquare, Square,
  Inbox, FolderPlus, FolderInput, Folder, Copy, CalendarClock, Mail, Play, Pencil, History, UserSquare2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const errMsg = (e, f) => { const d = e?.response?.data?.detail; return !d ? f : (typeof d === 'string' ? d : d.message || f); };

// Per-result panel cache — persists "Why this matters" / generated content / campaign
// across sub-tab navigation (component remounts) within the session.
const intelCardCache = {};
const SCOPES = [['', 'Any reach'], ['local', 'Local'], ['national', 'National (USA)'], ['global', 'Global']];

// Send a source into a target studio (Post / Video / Flyer) with the seed pre-loaded.
function seedAndGo(navigate, item, where) {
  const seed = { title: item.title, summary: item.summary, url: item.url, publisher: item.publisher };
  localStorage.setItem('dca_intel_seed', JSON.stringify(seed));
  localStorage.removeItem('dca_intel_seed_batch');
  if (where === 'flyer') navigate('/admin/flyer-studio?from=intel');
  else if (where === 'video') navigate('/admin/social-studio?from=intel&mode=video');
  else navigate('/admin/social-studio?from=intel&mode=post');
  const label = where === 'flyer' ? 'Flyer' : where === 'video' ? 'Video' : 'Post';
  toast.success(`Source sent to ${label} Studio`);
}

function seedBatchAndGo(navigate, items, where, label) {
  const sources = (items || []).map((it) => ({ title: it.title, summary: it.summary, url: it.url, publisher: it.publisher })).filter((s) => s.title);
  if (!sources.length) { toast.error('Nothing to send'); return; }
  localStorage.setItem('dca_intel_seed_batch', JSON.stringify({ label: label || 'Research folder', sources }));
  localStorage.removeItem('dca_intel_seed');
  navigate(`/admin/social-studio?from=intel&batch=1&mode=${where === 'video' ? 'video' : 'post'}`);
  toast.success(`Sent ${sources.length} source${sources.length > 1 ? 's' : ''} to ${where === 'video' ? 'Video' : 'Post'} Studio`);
}

const GOALS = ['Educate', 'Warn', 'Inspire', 'Promote service', 'Create urgency', 'Start conversation', 'Drive leads', 'Book consultation', 'Promote event', 'Build awareness', 'Explain a trend', 'Expose a problem', 'Strengthen authority', 'Generate campaign'];
const MODES = [
  ['broad_search', 'Broad Search'], ['current_events', 'Current Events'], ['recent_events', 'Recent Events'],
  ['historical_events', 'Historical Events'],
  ['research_search', 'Research Search'], ['news_search', 'News Search'], ['business_intel', 'Business Intelligence'],
  ['legal_search', 'Legal / Public Record'], ['education_search', 'Education Search'], ['gov_search', 'Government / Public Data'],
  ['video', 'Video / YouTube'],
];

function MultiSelectChips({ options, values, onChange, max = 3, testid }) {
  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else { if (values.length >= max) { toast.error(`Pick up to ${max}`); return; } onChange([...values, v]); }
  };
  return (
    <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
      {options.map(([v, l]) => { const on = values.includes(v); return (
        <button key={v} type="button" onClick={() => toggle(v)} data-testid={`${testid}-${v}`}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? 'bg-[#1E5BA8] text-white border-[#1E5BA8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1E5BA8]'}`}>{l}</button>
      ); })}
    </div>
  );
}
const SOURCE_TYPES = ['', 'Government', 'University / Academic', 'Major News Outlet', 'Trade Publication', 'Newswire'];

const CRED_COLORS = {
  'High Credibility': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Moderate Credibility': 'bg-amber-50 text-amber-700 border-amber-200',
  'Low Credibility': 'bg-orange-50 text-orange-700 border-orange-200',
  'Needs Verification': 'bg-rose-50 text-rose-700 border-rose-200',
};
const ACC_COLORS = {
  'Free Access': 'bg-emerald-50 text-emerald-700', 'Registration Required': 'bg-sky-50 text-sky-700',
  'Subscription Required': 'bg-rose-50 text-rose-700', 'Paywalled': 'bg-rose-50 text-rose-700', 'Unknown Access': 'bg-slate-100 text-slate-600',
};

function scoreColor(v) { return v >= 75 ? 'text-emerald-600' : v >= 55 ? 'text-amber-600' : 'text-slate-500'; }

export default function ContentIntelligence({ embedded = false }) {
  const [sub, setSub] = useState('search');
  const [profiles, setProfiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [unread, setUnread] = useState(0);

  const loadMeta = useCallback(async () => {
    try {
      const [p, u, b] = await Promise.all([
        api.get('/api/admin/social-studio/brand-profiles'),
        api.get('/api/intelligence/usage'),
        api.get('/api/intelligence/briefings').catch(() => ({ data: { unread: 0 } })),
      ]);
      setProfiles(p.data.profiles || []);
      setUsage(u.data);
      setUnread(b.data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const SUBS = [
    { id: 'search', label: 'Search', icon: Search },
    { id: 'watchlists', label: 'Watchlists', icon: Eye },
    { id: 'briefings', label: 'Briefings', icon: Inbox, badge: unread },
    { id: 'library', label: 'Library', icon: FolderOpen },
    { id: 'searches', label: 'Search Recipes', icon: ListChecks },
    { id: 'brands', label: 'Brand Profiles', icon: UserSquare2 },
  ];

  const Wrap = embedded ? 'div' : AdminLayout;

  return (
    <Wrap>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="intel-page">
        <StudioSuiteNav active="intelligence" embedded={embedded} />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1C] flex items-center gap-2"><Brain className="text-[#1E5BA8]" size={24} /> Content Intelligence Engine</h1>
            <p className="text-sm text-[#64748B] mt-1">Find credible public information and turn it into posts, scripts, flyers &amp; campaigns — the research brain for your studios.</p>
          </div>
          {usage && (
            <div className="text-xs font-bold px-3 py-2 rounded-lg bg-[#EFF6FF] text-[#1E5BA8]" data-testid="intel-usage">
              {usage.unlimited ? 'Admin · Unlimited searches' : `${usage.searches_remaining ?? usage.searches_cap} of ${usage.searches_cap} searches left today`}
            </div>
          )}
        </div>

        <div className="flex gap-1 border-b border-[#E2E8F0] mb-6 overflow-x-auto">
          {SUBS.map((s) => (
            <button key={s.id} onClick={() => setSub(s.id)} data-testid={`intel-subtab-${s.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${sub === s.id ? 'border-[#1E5BA8] text-[#1E5BA8]' : 'border-transparent text-[#64748B] hover:text-[#0A0F1C]'}`}>
              <s.icon size={16} /> {s.label}
              {s.badge > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold">{s.badge}</span>}
            </button>
          ))}
        </div>

        {sub === 'search' && <SearchView profiles={profiles} usage={usage} onSearched={loadMeta} />}
        {sub === 'library' && <LibraryView profiles={profiles} />}
        {sub === 'searches' && <SavedSearchesView profiles={profiles} />}
        {sub === 'watchlists' && <WatchlistsView profiles={profiles} onChanged={loadMeta} />}
        {sub === 'briefings' && <BriefingsView onChanged={loadMeta} />}
        {sub === 'brands' && <BrandProfileManager onChanged={loadMeta} />}
      </div>
    </Wrap>
  );
}

/* ============================ SEARCH ============================ */
function SearchView({ profiles, usage, onSearched, initial }) {
  const [topic, setTopic] = useState(initial?.topic || '');
  const [brandId, setBrandId] = useState(initial?.brand_profile_id || '');
  const [goals, setGoals] = useState(initial?.content_goals || (initial?.content_goal ? [initial.content_goal] : []));
  const [modes, setModes] = useState(initial?.modes || (initial?.mode ? [initial.mode] : ['broad_search']));
  const [country, setCountry] = useState(initial?.country || 'US');
  const [location, setLocation] = useState(initial?.location || '');
  const [scope, setScope] = useState(initial?.scope || '');
  const [showAdv, setShowAdv] = useState(false);
  const [mission, setMission] = useState('');
  const [audience, setAudience] = useState('');
  const [industry, setIndustry] = useState('');
  const [year, setYear] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);

  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('opportunity');
  const [credFilter, setCredFilter] = useState('');
  const [selected, setSelected] = useState({});
  const [campaign, setCampaign] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [libFolders, setLibFolders] = useState([]);
  const [savePicker, setSavePicker] = useState(null); // results array pending save
  const navigate = useNavigate();

  const openSavePicker = async (toSave) => {
    if (!toSave.length) { toast.error('Nothing to save'); return; }
    try { const r = await api.get('/api/intelligence/folders'); setLibFolders(r.data.folders || []); } catch { setLibFolders([]); }
    setSavePicker(toSave);
  };
  const createLibFolder = async (name) => {
    try { const r = await api.post('/api/intelligence/folders', { name }); setLibFolders((s) => [...s, r.data.folder]); return r.data.folder; }
    catch (e) { toast.error(errMsg(e, 'Could not create folder')); return null; }
  };
  const doSaveToLibrary = async (folderId) => {
    const toSave = savePicker || []; setSavePicker(null);
    try {
      const r = await api.post('/api/intelligence/library/bulk-save', { folder_id: folderId, results: toSave });
      toast.success(`Saved ${r.data.saved} to Library${folderId ? '' : ' (Unfiled)'}${r.data.saved < toSave.length ? ` · ${toSave.length - r.data.saved} already saved` : ''}`);
    } catch (e) { toast.error(errMsg(e, 'Could not save to Library')); }
  };

  const filters = () => ({ topic, brand_profile_id: brandId || null, content_goal: goals[0] || '', content_goals: goals, mode: modes[0] || 'broad_search', modes, country, location, scope, mission, audience, industry, year, date_from: dateFrom, date_to: dateTo, source_type: sourceType, free_only: freeOnly });

  const runSearch = async (override) => {
    const f = override || filters();
    if (!f.topic?.trim()) { toast.error('Enter a topic or phrase to search'); return; }
    setBusy(true); setData(null); setSelected({});
    try {
      const r = await api.post('/api/intelligence/search', f);
      setData(r.data); setActiveTab('all');
      if (r.data.message) toast.info(r.data.message);
      onSearched?.();
    } catch (e) { toast.error(errMsg(e, 'Search failed')); } finally { setBusy(false); }
  };

  useEffect(() => { if (initial?.autorun) runSearch(initial); /* eslint-disable-next-line */ }, []);

  const saveSearch = async () => {
    const name = window.prompt('Name this saved search:', topic);
    if (!name) return;
    try { await api.post('/api/intelligence/saved-searches', { search_name: name, filters: filters(), brand_profile_id: brandId || null }); toast.success('Saved search created'); }
    catch (e) { toast.error(errMsg(e, 'Could not save search')); }
  };

  let results = data?.results || [];
  if (activeTab === 'paywalled') results = results.filter((r) => r.tab_extra === 'paywalled');
  else if (activeTab !== 'all') results = results.filter((r) => r.tab === activeTab);
  if (credFilter) results = results.filter((r) => r.credibility_rating === credFilter);
  results = [...results].sort((a, b) => {
    if (sortBy === 'newest') return 0;
    if (sortBy === 'viral') return b.viral_potential_score - a.viral_potential_score;
    if (sortBy === 'lead') return b.lead_generation_score - a.lead_generation_score;
    if (sortBy === 'credibility') return b.authority_potential_score - a.authority_potential_score;
    if (sortBy === 'free') return (a.accessibility_status === 'Free Access' ? -1 : 1);
    return b.opportunity_score - a.opportunity_score;
  });

  const selectedList = (data?.results || []).filter((r) => selected[r.result_id]);

  const analyze = async () => {
    if (selectedList.length < 2) { toast.error('Select at least 2 sources'); return; }
    setAnalysis({ loading: true });
    try { const r = await api.post('/api/intelligence/analyze', { brand_profile_id: brandId || null, audience, sources: selectedList }); setAnalysis(r.data.analysis); }
    catch (e) { setAnalysis(null); toast.error(errMsg(e, 'Analysis failed')); }
  };
  const buildCampaign = async (sources) => {
    setCampaign({ loading: true });
    try { const r = await api.post('/api/intelligence/campaign', { brand_profile_id: brandId || null, audience, mission, sources }); setCampaign(r.data.campaign); }
    catch (e) { setCampaign(null); toast.error(errMsg(e, 'Campaign build failed')); }
  };

  return (
    <div data-testid="intel-search-view">
      {/* form */}
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-5 mb-6">
        <div className="grid md:grid-cols-[1fr_220px] gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Topic, phrase, question, mission or idea</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="intel-topic" onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="e.g. small business layoffs, youth literacy, final expense planning…" className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Brand Profile</label>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="intel-brand-select" className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[#E2E8F0] text-sm">
              <option value="">— None —</option>
              {groupedProfileOptions(profiles)}
            </select>
          </div>
        </div>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Content goals <span className="text-[#94A3B8] normal-case font-semibold">(pick up to 3)</span></label>
            <MultiSelectChips options={GOALS.map((g) => [g, g])} values={goals} onChange={setGoals} max={3} testid="intel-goals" />
          </div>
          <div className="grid sm:grid-cols-[1fr_240px] gap-3 items-start">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Search mode <span className="text-[#94A3B8] normal-case font-semibold">(pick up to 3 · includes Video / YouTube)</span></label>
              <MultiSelectChips options={MODES} values={modes} onChange={setModes} max={3} testid="intel-modes" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">Location {scope === 'local' && <span className="text-[#1E5BA8] normal-case font-semibold">— enter your city/region</span>}</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="intel-location" placeholder="Savannah, Georgia / USA" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            </div>
          </div>
        </div>

        {/* Global filters — Specific Year, Custom Date Range & Location scope are available on EVERY search */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 p-3 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE]" data-testid="intel-global-filters">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><Filter size={12} /> Location reach</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="intel-scope" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm">
              {SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><CalendarClock size={12} /> Specific year</label>
            <input value={year} onChange={(e) => setYear(e.target.value)} data-testid="intel-year" placeholder="e.g. 2023" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><CalendarClock size={12} /> Date from</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="intel-date-from" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><CalendarClock size={12} /> Date to</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="intel-date-to" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm" />
          </div>
        </div>

        <button onClick={() => setShowAdv((s) => !s)} data-testid="intel-advanced-toggle" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#1E5BA8]">
          {showAdv ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced filters
        </button>
        {showAdv && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 p-3 rounded-xl bg-[#F8FAFC] border border-[#F1F5F9]" data-testid="intel-advanced">
            <input value={mission} onChange={(e) => setMission(e.target.value)} placeholder="Mission / vision / brand identity" data-testid="intel-mission" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            <input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Target audience" data-testid="intel-audience" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry / niche" data-testid="intel-industry" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
            <select value={sourceType} onChange={(e) => setSourceType(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
              {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s || 'Any source type'}</option>)}
            </select>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
              {['US', 'GB', 'CA', 'AU'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm font-semibold text-[#475569]"><input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} data-testid="intel-freeonly" /> Free sources only</label>
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={() => runSearch()} disabled={busy} data-testid="intel-search-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
          </button>
          {data && <button onClick={saveSearch} data-testid="intel-save-search" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-sm font-bold hover:border-[#1E5BA8]"><Save size={15} /> Save this search</button>}
        </div>
      </div>

      {busy && <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" size={28} /><p className="text-sm text-[#94A3B8] mt-2">Scanning credible public sources…</p></div>}

      {data && !busy && (
        <>
          {data.total === 0 ? (
            <div className="py-16 text-center text-sm text-[#64748B]" data-testid="intel-noresults">{data.message}</div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_300px] gap-6">
              <div>
                {/* tabs */}
                <div className="flex gap-1.5 flex-wrap mb-3" data-testid="intel-tabs">
                  {data.tabs.map((t) => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`intel-tab-${t.key}`}
                      className={`px-2.5 py-1.5 rounded-full text-xs font-bold border ${activeTab === t.key ? 'bg-[#1E5BA8] text-white border-[#1E5BA8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1E5BA8]'}`}>
                      {t.label} ({t.count})
                    </button>
                  ))}
                </div>
                {/* sort + cred filter + analyze */}
                <div className="flex items-center gap-2 flex-wrap mb-4 text-sm">
                  <Filter size={14} className="text-[#94A3B8]" />
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="intel-sort" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs font-semibold">
                    <option value="opportunity">Best content opportunity</option>
                    <option value="viral">Highest viral potential</option>
                    <option value="lead">Highest lead generation</option>
                    <option value="credibility">Highest authority</option>
                    <option value="free">Free access first</option>
                  </select>
                  <select value={credFilter} onChange={(e) => setCredFilter(e.target.value)} data-testid="intel-cred-filter" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs font-semibold">
                    <option value="">All credibility</option>
                    {Object.keys(CRED_COLORS).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {selectedList.length > 0 && (
                    <>
                      <button onClick={analyze} data-testid="intel-analyze-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0F766E] text-white text-xs font-bold hover:bg-[#0d655e]"><Layers size={13} /> Analyze {selectedList.length} sources</button>
                      <button onClick={() => buildCampaign(selectedList)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white text-xs font-bold hover:bg-[#6D28D9]"><Megaphone size={13} /> Campaign from selected</button>
                    </>
                  )}
                  <button onClick={() => openSavePicker(selectedList.length ? selectedList : results)} data-testid="intel-save-to-library" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold hover:bg-[#1A4F94] ml-auto"><Bookmark size={13} /> {selectedList.length ? `Save ${selectedList.length} to Library` : `Save all to Library`}</button>
                </div>

                <div className="space-y-3">
                  {results.map((r) => (
                    <ResultCard key={r.result_id} r={r} brandId={brandId} audience={audience} mission={mission}
                      selected={!!selected[r.result_id]} onToggle={() => setSelected((s) => ({ ...s, [r.result_id]: !s[r.result_id] }))} />
                  ))}
                </div>
              </div>

              {/* sidebar */}
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4" data-testid="intel-diversity">
                  <div className="text-xs font-bold uppercase tracking-wide text-[#64748B] mb-2 flex items-center gap-1.5"><BarChart3 size={14} /> Source Diversity</div>
                  <div className="text-2xl font-bold text-[#0A0F1C]">{data.diversity.rating}</div>
                  <div className="text-xs text-[#64748B] mt-2 space-y-0.5">
                    <div>Sources analyzed: <b>{data.diversity.sources_analyzed}</b></div>
                    <div>Unique publishers: <b>{data.diversity.unique_publishers}</b></div>
                    <div>Duplicates removed: <b>{data.diversity.duplicates_removed}</b></div>
                    <div>Source-type variety: <b>{data.diversity.source_type_variety}</b></div>
                  </div>
                </div>
                {data.recommendations?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-[#64748B] mb-2 flex items-center gap-1.5"><Lightbulb size={14} /> You may also want to explore</div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.recommendations.map((rec, i) => (
                        <button key={i} data-testid={`intel-rec-${i}`} onClick={() => { setTopic(rec); runSearch({ ...filters(), topic: rec }); }}
                          className="px-2.5 py-1 rounded-full bg-[#EFF6FF] text-[#1E5BA8] text-xs font-semibold hover:bg-[#DBEAFE]">{rec}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {campaign && <CampaignModal data={campaign} onClose={() => setCampaign(null)} />}
      {analysis && <AnalysisModal data={analysis} onClose={() => setAnalysis(null)} />}
      {savePicker && <FolderPickerModal folders={libFolders} title={`Save ${savePicker.length} source${savePicker.length > 1 ? 's' : ''} to Library`} onClose={() => setSavePicker(null)} onPick={doSaveToLibrary} onCreate={createLibFolder} />}
    </div>
  );
}

/* ============================ RESULT CARD ============================ */
function ResultCard({ r, brandId, audience, mission, selected, onToggle, variant = 'search', lib }) {
  const navigate = useNavigate();
  const cache = intelCardCache[r.result_id] || {};
  const [open, setOpen] = useState(cache.open || false);
  const [enrich, setEnrich] = useState(cache.enrich || null);
  const [made, setMade] = useState(cache.made || null);
  const [campaign, setCampaign] = useState(cache.campaign || null);
  const [saved, setSaved] = useState(cache.saved || false);
  const [busy, setBusy] = useState('');
  const [turnOpen, setTurnOpen] = useState(false);

  useEffect(() => {
    intelCardCache[r.result_id] = { open, enrich, made, campaign, saved };
  }, [open, enrich, made, campaign, saved, r.result_id]);

  const doEnrich = async () => {
    setOpen(true); setBusy('enrich');
    try { const res = await api.post('/api/intelligence/enrich', { brand_profile_id: brandId || null, audience, mission, result: r }); setEnrich(res.data.enrichment); }
    catch (e) { toast.error(errMsg(e, 'Enrichment failed')); } finally { setBusy(''); }
  };
  const save = async () => {
    setBusy('save');
    try { await api.post('/api/intelligence/library', { brand_profile_id: brandId || null, result: { ...r, content_angles: enrich?.angles } }); setSaved(true); toast.success('Saved to Intelligence Library'); }
    catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(''); }
  };
  const turnInto = async (output) => {
    setOpen(true); setBusy(output);
    try { const res = await api.post('/api/intelligence/to-content', { brand_profile_id: brandId || null, audience, output, result: r }); setMade({ output, ...res.data }); }
    catch (e) { toast.error(errMsg(e, 'Generation failed')); } finally { setBusy(''); }
  };
  const doCampaign = async () => {
    setOpen(true); setBusy('campaign');
    try { const res = await api.post('/api/intelligence/campaign', { brand_profile_id: brandId || null, audience, mission, sources: [r] }); setCampaign(res.data.campaign); }
    catch (e) { toast.error(errMsg(e, 'Campaign build failed')); } finally { setBusy(''); }
  };

  const Score = ({ label, v, Icon }) => (
    <div className="text-center" title={label}>
      <div className={`text-base font-bold ${scoreColor(v)}`}>{v}</div>
      <div className="text-[9px] uppercase tracking-wide text-[#94A3B8] flex items-center justify-center gap-0.5"><Icon size={9} /> {label}</div>
    </div>
  );
  const CBlock = ({ label, val }) => val ? (
    <div className="mb-2"><div className="text-[10px] font-bold uppercase tracking-wide text-[#7C3AED]">{label}</div>
      <div className="text-xs text-[#334155] whitespace-pre-wrap mt-0.5">{Array.isArray(val) ? val.map((x, i) => <div key={i}>• {typeof x === 'string' ? x : JSON.stringify(x)}</div>) : (typeof val === 'string' ? val : JSON.stringify(val, null, 2))}</div>
    </div>
  ) : null;

  return (
    <div className={`bg-white rounded-2xl border p-4 ${(variant === 'library' ? lib?.selected : selected) ? 'border-[#1E5BA8] ring-1 ring-[#1E5BA8]' : 'border-[#E2E8F0]'}`} data-testid={`intel-result-${r.result_id || r.item_id}`}>
      <div className="flex gap-3">
        {(variant === 'search' || (variant === 'library' && lib?.onToggle)) && (
          <button onClick={variant === 'search' ? onToggle : lib.onToggle} data-testid={variant === 'search' ? `intel-select-${r.result_id}` : `intel-lib-select-${r.item_id}`} className="mt-0.5 text-[#1E5BA8] flex-shrink-0" aria-label="Select source">
            {(variant === 'search' ? selected : lib.selected) ? <CheckSquare size={18} /> : <Square size={18} className="text-[#CBD5E1]" />}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-bold text-[#0A0F1C] text-sm leading-snug">{r.title}</h3>
            {variant === 'library' && (
              <button onClick={lib.onDelete} className="text-[#94A3B8] hover:text-[#DC2626] flex-shrink-0" aria-label="Delete" data-testid={`intel-lib-del-${r.item_id}`}><Trash2 size={15} /></button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[11px]">
            <span className="font-semibold text-[#475569]">{r.publisher}</span>
            {r.published_date && <span className="text-[#94A3B8]">· {String(r.published_date).slice(0, 16)}</span>}
            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{r.content_type}</span>
            <span className={`px-1.5 py-0.5 rounded border font-semibold ${CRED_COLORS[r.credibility_rating] || ''}`}>{r.credibility_rating}</span>
            <span className={`px-1.5 py-0.5 rounded font-semibold ${ACC_COLORS[r.accessibility_status] || ''}`}>{r.accessibility_status}</span>
            {variant === 'library' && lib.folderLabel && <span className="px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1E5BA8] font-bold inline-flex items-center gap-1"><FolderOpen size={10} /> {lib.folderLabel}</span>}
          </div>
          {r.summary && <p className="text-xs text-[#64748B] mt-2 line-clamp-2">{r.summary}</p>}

          <div className="grid grid-cols-5 gap-1 mt-3 py-2 border-y border-[#F1F5F9]">
            <Score label="Opp." v={r.opportunity_score} Icon={Target} />
            <Score label="Viral" v={r.viral_potential_score} Icon={TrendingUp} />
            <Score label="Lead" v={r.lead_generation_score} Icon={Megaphone} />
            <Score label="Auth." v={r.authority_potential_score} Icon={Award} />
            <Score label="Rel." v={r.relevance_score} Icon={ShieldCheck} />
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3 items-center">
            <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`intel-open-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold hover:bg-[#1A4F94]"><ExternalLink size={12} /> Open Source</a>
            {variant === 'search' && (
              <button onClick={save} disabled={saved || busy === 'save'} data-testid={`intel-save-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8] disabled:opacity-60"><Bookmark size={12} /> {saved ? 'Saved' : 'Save'}</button>
            )}
            <button onClick={doEnrich} data-testid={`intel-enrich-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#7C3AED] text-[#7C3AED] text-xs font-bold hover:bg-[#F5F3FF]"><Sparkles size={12} /> Why this matters</button>
            <button onClick={doCampaign} disabled={busy === 'campaign'} data-testid={`intel-campaign-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#7C3AED] text-[#7C3AED] text-xs font-bold hover:bg-[#F5F3FF] disabled:opacity-60"><Megaphone size={12} /> Build Campaign</button>
            <div className="relative">
              <button onClick={() => setTurnOpen((o) => !o)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]" data-testid={`intel-turn-${r.result_id}`}><Wand2 size={12} /> Turn into ▾</button>
              {turnOpen && (
                <div className="absolute z-20 bg-white border border-[#E2E8F0] rounded-lg shadow-lg p-1 mt-0.5 w-44" data-testid={`intel-turn-menu-${r.result_id}`}>
                  {[['post', 'Post'], ['video_script', 'Video Script'], ['carousel', 'Carousel'], ['facebook_live', 'FB Live Outline'], ['flyer', 'Flyer Copy']].map(([k, l]) => (
                    <button key={k} onClick={() => { setTurnOpen(false); turnInto(k); }} data-testid={`intel-into-${k}-${r.result_id}`} className="block w-full text-left px-2 py-1.5 text-xs font-semibold text-[#475569] hover:bg-[#F1F5F9] rounded">{l}</button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => sendTo('video')} data-testid={`intel-send-video-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]"><Send size={12} /> Video Studio</button>
            <button onClick={() => sendTo('social')} data-testid={`intel-send-social-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]"><Send size={12} /> Post Studio</button>
            <button onClick={() => sendTo('flyer')} data-testid={`intel-send-flyer-${r.result_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]"><Send size={12} /> Flyer Studio</button>
            {variant === 'library' && (
              <button onClick={lib.onMoveClick} data-testid={`intel-lib-move-${r.item_id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8] ml-auto"><FolderInput size={12} /> Move to folder</button>
            )}
          </div>

          {open && (
            <div className="mt-3 rounded-xl bg-[#F8FAFC] border border-[#F1F5F9] p-3" data-testid={`intel-panel-${r.result_id}`}>
              {busy && <div className="flex items-center gap-2 text-xs text-[#64748B]"><Loader2 size={14} className="animate-spin" /> Working…</div>}
              {enrich && (
                <div className="space-y-2 text-xs">
                  <div><b className="text-[#1E5BA8]">Why this matters:</b> {enrich.why_this_matters}</div>
                  <div><b className="text-[#1E5BA8]">Why should I care:</b> {enrich.why_should_i_care}</div>
                  {enrich.angles && (
                    <div className="grid sm:grid-cols-2 gap-1.5 mt-1">
                      {Object.entries(enrich.angles).map(([k, v]) => (
                        <div key={k} className="bg-white rounded-lg p-2 border border-[#F1F5F9]"><span className="font-bold text-[#7C3AED] capitalize">{k.replace(/_/g, ' ')}:</span> {v}</div>
                      ))}
                    </div>
                  )}
                  {/* Viral formula: Hook -> Value -> CTA */}
                  <div className="rounded-lg border border-[#DBEAFE] bg-[#EFF6FF] p-2 space-y-1.5" data-testid={`intel-formula-${r.result_id}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-[#1E5BA8]">Viral formula · under 60 seconds</div>
                    {enrich.suggested_hook && <div><b className="text-[#1E5BA8]">1 · Hook (first 3s):</b> {enrich.suggested_hook}</div>}
                    {enrich.suggested_value && <div data-testid={`intel-value-${r.result_id}`}><b className="text-[#1E5BA8]">2 · Value (problem → solution):</b> {enrich.suggested_value}</div>}
                    {enrich.suggested_cta && <div><b className="text-[#1E5BA8]">3 · CTA (last 5-10s):</b> {enrich.suggested_cta}</div>}
                  </div>
                </div>
              )}
              {made && (
                <div className="mt-2">
                  <div className="text-xs font-bold text-[#0A0F1C] mb-1 capitalize">{made.output.replace(/_/g, ' ')}</div>
                  <pre className="text-xs whitespace-pre-wrap bg-white rounded-lg p-2 border border-[#F1F5F9] text-[#334155]" data-testid={`intel-made-${r.result_id}`}>{typeof made.content === 'string' ? made.content : JSON.stringify(made.content, null, 2)}</pre>
                  <button onClick={() => { navigator.clipboard.writeText(typeof made.content === 'string' ? made.content : JSON.stringify(made.content)); toast.success('Copied'); }} className="mt-1 text-xs font-bold text-[#1E5BA8]">Copy</button>
                  <p className="text-[10px] text-[#94A3B8] mt-1">{made.citation}</p>
                </div>
              )}
              {campaign && !campaign.loading && (
                <div className="mt-3 border-t border-[#E2E8F0] pt-2" data-testid={`intel-campaign-result-${r.result_id}`}>
                  <div className="text-xs font-bold text-[#7C3AED] mb-1.5">Content Campaign (from this source)</div>
                  <CBlock label="Facebook Post" val={campaign.facebook_post} />
                  <CBlock label="Instagram Caption" val={campaign.instagram_caption} />
                  <CBlock label="Short-Form Video Script" val={campaign.video_script} />
                  <CBlock label="Hooks" val={campaign.hooks} />
                  <CBlock label="CTAs" val={campaign.ctas} />
                  <CBlock label="7-Day Content Calendar" val={campaign.content_calendar} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  function sendTo(where) { seedAndGo(navigate, r, where); }
}

/* ============================ MODALS ============================ */
function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => { const k = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid={testid} onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-3xl'} p-6 max-h-[92vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-bold text-[#0A0F1C]">{title}</h3><button onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} /></button></div>
        {children}
      </div>
    </div>
  );
}

function CampaignModal({ data, onClose }) {
  if (data.loading) return <Modal title="Building campaign…" onClose={onClose} testid="intel-campaign-modal"><div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-[#7C3AED]" /></div></Modal>;
  if (data._raw) return <Modal title="Content Campaign" onClose={onClose} testid="intel-campaign-modal"><pre className="text-xs whitespace-pre-wrap">{data._raw}</pre></Modal>;
  const Block = ({ label, val }) => val ? (
    <div className="mb-3"><div className="text-[11px] font-bold uppercase tracking-wide text-[#7C3AED]">{label}</div>
      <div className="text-sm text-[#334155] whitespace-pre-wrap mt-0.5">{Array.isArray(val) ? val.map((x, i) => <div key={i}>• {typeof x === 'string' ? x : JSON.stringify(x)}</div>) : (typeof val === 'string' ? val : JSON.stringify(val, null, 2))}</div>
    </div>
  ) : null;
  return (
    <Modal title="Content Campaign (from your sources)" onClose={onClose} testid="intel-campaign-modal">
      <Block label="Facebook Post" val={data.facebook_post} />
      <Block label="Instagram Caption" val={data.instagram_caption} />
      <Block label="LinkedIn Post" val={data.linkedin_post} />
      <Block label="Short-Form Video Script" val={data.video_script} />
      <Block label="Facebook Live Outline" val={data.facebook_live_outline} />
      <Block label="Carousel Outline" val={data.carousel_outline} />
      <Block label="Flyer Concept" val={data.flyer_concept} />
      <Block label="Email Idea" val={data.email_idea} />
      <Block label="Hooks" val={data.hooks} />
      <Block label="CTAs" val={data.ctas} />
      <Block label="Talking Points" val={data.talking_points} />
      <Block label="Lead Magnet Idea" val={data.lead_magnet_idea} />
      <Block label="7-Day Content Calendar" val={data.content_calendar} />
    </Modal>
  );
}

function AnalysisModal({ data, onClose }) {
  if (data.loading) return <Modal title="Analyzing sources…" onClose={onClose} testid="intel-analysis-modal"><div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-[#0F766E]" /></div></Modal>;
  const Block = ({ label, val }) => val ? (
    <div className="mb-3"><div className="text-[11px] font-bold uppercase tracking-wide text-[#0F766E]">{label}</div>
      <div className="text-sm text-[#334155] mt-0.5">{Array.isArray(val) ? val.map((x, i) => <div key={i}>• {typeof x === 'string' ? x : JSON.stringify(x)}</div>) : val}</div></div>
  ) : null;
  return (
    <Modal title="Multi-Source Intelligence" onClose={onClose} testid="intel-analysis-modal">
      <Block label="Common Themes" val={data.common_themes} />
      <Block label="Repeated Problems" val={data.repeated_problems} />
      <Block label="Emerging Trends" val={data.emerging_trends} />
      <Block label="Warnings" val={data.warnings} />
      <Block label="Opportunities" val={data.opportunities} />
      <Block label="Audience Impact" val={data.audience_impact} />
      <Block label="Business Opportunity" val={data.business_opportunity} />
      <Block label="Top Statistics" val={data.top_statistics} />
      <Block label="Content Ideas" val={data.content_ideas} />
      <Block label="Suggested Campaign Themes" val={data.suggested_campaign_themes} />
      <Block label="Recommended 7-Day Series" val={data.recommended_series} />
    </Modal>
  );
}

/* ============================ LIBRARY (with folders) ============================ */
function CalendarModal({ days, sourcesUsed, onClose, onSend }) {
  return (
    <Modal title="7-Day Content Calendar" onClose={onClose} testid="intel-calendar-modal" wide>
      <p className="text-xs text-[#64748B] mb-3">Grounded in {sourcesUsed} saved source{sourcesUsed === 1 ? '' : 's'} from this folder. Review, copy, or send any day straight to Post Studio.</p>
      <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
        {days.map((d, i) => (
          <div key={i} className="border border-[#E2E8F0] rounded-xl p-3" data-testid={`intel-cal-day-${i}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-bold text-sm text-[#0A0F1C]">{d.day || `Day ${i + 1}`}</div>
              <div className="flex items-center gap-1.5 text-[10px]">
                {d.platform && <span className="px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E5BA8] font-bold">{d.platform}</span>}
                {d.format && <span className="px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569] font-bold">{d.format}</span>}
              </div>
            </div>
            {d.theme && <div className="text-xs font-semibold text-[#334155] mt-1">{d.theme}</div>}
            {d.hook && <p className="text-sm text-[#0A0F1C] mt-1.5"><b className="text-[#1E5BA8]">Hook:</b> {d.hook}</p>}
            {d.caption && <p className="text-sm text-[#475569] mt-1">{d.caption}</p>}
            {d.cta && <p className="text-xs text-[#0F766E] font-semibold mt-1">CTA: {d.cta}</p>}
            {d.source_title && <p className="text-[10px] text-[#94A3B8] mt-1">From: {d.source_title}</p>}
            <div className="flex gap-2 mt-2">
              <button onClick={() => onSend(d)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0F766E] text-white text-[11px] font-bold" data-testid={`intel-cal-send-${i}`}><Send size={12} /> Send to Post Studio</button>
              <button onClick={() => { navigator.clipboard.writeText(`${d.hook || ''}\n\n${d.caption || ''}\n\n${d.cta || ''}`.trim()); toast.success('Copied'); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#E2E8F0] text-[#475569] text-[11px] font-bold"><Copy size={12} /> Copy</button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function LibraryView() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [path, setPath] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [picker, setPicker] = useState(null);
  const [newFolder, setNewFolder] = useState(null);
  const [dragId, setDragId] = useState(null);

  const currentFolder = path.length ? path[path.length - 1] : null;
  const currentFolderId = currentFolder?.folder_id || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, fl] = await Promise.all([
        api.get('/api/intelligence/library'),
        api.get('/api/intelligence/folders').catch(() => ({ data: { folders: [] } })),
      ]);
      setItems(it.data.items || []); setFolders(fl.data.folders || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [currentFolderId]);

  const folderName = (id) => (id ? (folders.find((f) => f.folder_id === id)?.name || 'folder') : 'Unfiled');
  const validIds = new Set(folders.map((f) => f.folder_id));
  const effFolder = (i) => (validIds.has(i.folder_id) ? i.folder_id : null); // orphaned/legacy ids -> Unfiled
  const childFolders = folders.filter((f) => (f.parent_folder_id || null) === currentFolderId);
  let shown = items.filter((i) => effFolder(i) === currentFolderId);
  if (q) shown = shown.filter((i) => (i.title + (i.summary || '')).toLowerCase().includes(q.toLowerCase()));

  const createFolder = async (name) => {
    try { await api.post('/api/intelligence/folders', { name, parent_folder_id: newFolder?.parent_folder_id || null }); setNewFolder(null); toast.success('Folder created'); await load(); }
    catch (e) { toast.error(errMsg(e, 'Could not create folder')); }
  };
  const createFolderInline = async (name) => {
    try { const r = await api.post('/api/intelligence/folders', { name, parent_folder_id: currentFolderId }); setFolders((s) => [...s, r.data.folder]); return r.data.folder; }
    catch (e) { toast.error('Could not create folder'); return null; }
  };
  const renameFolder = async (f) => {
    const name = window.prompt('Rename folder', f.name); if (!name || !name.trim()) return;
    try { await api.put(`/api/intelligence/folders/${f.folder_id}`, { name: name.trim() }); setPath((p) => p.map((x) => (x.folder_id === f.folder_id ? { ...x, name: name.trim() } : x))); await load(); }
    catch (e) { toast.error('Could not rename'); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Items move back to Unfiled and subfolders move up — nothing is deleted.`)) return;
    try { await api.delete(`/api/intelligence/folders/${f.folder_id}`); toast.success('Folder deleted — contents kept'); setPath((p) => p.filter((x) => x.folder_id !== f.folder_id)); await load(); }
    catch (e) { toast.error('Could not delete folder'); }
  };
  const dropFolder = async (targetId) => {
    const ids = childFolders.map((f) => f.folder_id);
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const from = ids.indexOf(dragId); const to = ids.indexOf(targetId); setDragId(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setFolders((prev) => { const sib = prev.filter((f) => (f.parent_folder_id || null) === currentFolderId); const others = prev.filter((f) => (f.parent_folder_id || null) !== currentFolderId); const re = ids.map((id) => sib.find((f) => f.folder_id === id)).filter(Boolean); return [...others, ...re]; });
    try { await api.post('/api/intelligence/folders/reorder', { ordered_ids: ids }); await load(); }
    catch (e) { toast.error('Could not reorder'); await load(); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = shown.length > 0 && shown.every((i) => selected.has(i.item_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(shown.map((i) => i.item_id)));

  const doMove = async (folderId) => {
    const cfg = picker; setPicker(null); if (!cfg) return;
    try {
      const ids = cfg.kind === 'item' ? [cfg.id] : cfg.ids;
      await api.post('/api/intelligence/library/bulk-move', { item_ids: ids, folder_id: folderId });
      if (cfg.kind !== 'item') setSelected(new Set());
      toast.success(`Moved to ${folderName(folderId)}`); await load();
    } catch (e) { toast.error(errMsg(e, 'Could not move')); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
    try { await api.post('/api/intelligence/library/bulk-delete', { item_ids: [...selected] }); toast.success(`Deleted ${selected.size}`); setSelected(new Set()); await load(); }
    catch (e) { toast.error('Could not delete'); }
  };
  const delOne = async (id) => { try { await api.delete(`/api/intelligence/library/${id}`); setItems((s) => s.filter((x) => x.item_id !== id)); } catch { toast.error('Could not delete'); } };
  const sendAll = (where) => { if (shown.length) seedBatchAndGo(navigate, shown, where, currentFolder?.name || 'Library'); };

  const [cal, setCal] = useState(null);
  const [calBusy, setCalBusy] = useState(false);
  const buildCalendar = async () => {
    setCalBusy(true);
    try {
      const r = await api.post('/api/intelligence/library/folder-calendar', { folder_id: currentFolderId });
      if (!r.data.calendar?.length) { toast.error('Could not generate the calendar — please try again.'); return; }
      setCal({ days: r.data.calendar, sourcesUsed: r.data.sources_used });
    } catch (e) { toast.error(errMsg(e, 'Could not build calendar')); } finally { setCalBusy(false); }
  };
  const sendDay = (d) => seedAndGo(navigate, { title: d.hook || d.theme || 'Content idea', summary: [d.caption, d.cta].filter(Boolean).join('\n\n'), url: '', publisher: '' }, 'post');

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;
  return (
    <div data-testid="intel-library">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-1 text-sm flex-wrap">
          <button onClick={() => setPath([])} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg font-bold ${currentFolder ? 'text-[#475569] hover:bg-[#F1F5F9]' : 'text-[#1E5BA8] bg-[#EFF6FF]'}`} data-testid="intel-lib-breadcrumb-root"><FolderOpen size={14} /> Library</button>
          {path.map((f, i) => (<span key={f.folder_id} className="flex items-center gap-1"><ChevronRight size={13} className="text-[#CBD5E1]" /><button onClick={() => setPath((p) => p.slice(0, i + 1))} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg font-bold ${i === path.length - 1 ? 'text-[#1E5BA8] bg-[#EFF6FF]' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}><FolderOpen size={14} /> {f.name}</button></span>))}
        </div>
        <div className="flex items-center gap-2">
          {currentFolder && (<>
            <button onClick={() => renameFolder(currentFolder)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]" data-testid="intel-lib-folder-rename"><Pencil size={12} /> Rename</button>
            <button onClick={() => deleteFolder(currentFolder)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#FCA5A5] text-[#DC2626] text-xs font-bold hover:bg-red-50" data-testid="intel-lib-folder-delete"><Trash2 size={12} /> Delete folder</button>
          </>)}
          <button onClick={() => setNewFolder({ parent_folder_id: currentFolderId, parentName: currentFolder?.name || null })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold" data-testid="intel-lib-newfolder"><FolderPlus size={14} /> {currentFolder ? 'New subfolder' : 'New folder'}</button>
        </div>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search saved intelligence…" data-testid="intel-lib-search" className="w-full max-w-md mb-4 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />

      {childFolders.length > 0 && (<>
        {childFolders.length > 1 && <div className="text-[10px] text-[#94A3B8] mb-1 ml-0.5">Tip: drag folders to reorder them.</div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="intel-lib-folder-grid">
          {childFolders.map((f) => { const subs = folders.filter((x) => x.parent_folder_id === f.folder_id).length; return (
            <div key={f.folder_id} draggable onDragStart={() => setDragId(f.folder_id)} onDragOver={(e) => e.preventDefault()} onDrop={() => dropFolder(f.folder_id)} onDragEnd={() => setDragId(null)} onClick={() => setPath((p) => [...p, f])} className={`flex items-center gap-2 p-3 rounded-xl border bg-white hover:border-[#1E5BA8] hover:shadow-sm text-left cursor-pointer transition-colors ${dragId === f.folder_id ? 'opacity-40 border-dashed border-[#1E5BA8]' : 'border-[#E2E8F0]'}`} data-testid={`intel-lib-folder-${f.folder_id}`}>
              <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0"><FolderOpen size={18} className="text-[#1E5BA8]" /></div>
              <div className="min-w-0 flex-1"><div className="font-bold text-sm text-[#0A0F1C] truncate">{f.name}</div><div className="text-[11px] text-[#94A3B8]">{f.item_count || 0} item{(f.item_count || 0) === 1 ? '' : 's'}{subs ? ` · ${subs} subfolder${subs === 1 ? '' : 's'}` : ''}</div></div>
              <ChevronRight size={16} className="text-[#CBD5E1]" />
            </div>); })}
        </div>
      </>)}

      {currentFolder != null && shown.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2 p-2.5 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="intel-lib-sendall">
          <span className="text-[11px] font-bold text-[#15803D] flex items-center gap-1.5"><Sparkles size={13} /> Turn all {shown.length} item{shown.length > 1 ? 's' : ''} into content</span>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={buildCalendar} disabled={calBusy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1E5BA8] text-white text-[11px] font-bold hover:bg-[#16467f] disabled:opacity-60" data-testid="intel-lib-calendar">{calBusy ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />} Build 7-day calendar</button>
            <button onClick={() => sendAll('post')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0F766E] text-white text-[11px] font-bold hover:bg-[#0d655e]"><Send size={12} /> Send all → Post Studio</button>
            <button onClick={() => sendAll('video')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#7C3AED] text-white text-[11px] font-bold hover:bg-[#6D28D9]"><Wand2 size={12} /> Send all → Video Studio</button>
          </div>
        </div>
      )}

      {shown.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-2 p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="intel-lib-toolbar">
          <button onClick={toggleAll} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#475569] hover:text-[#1E5BA8]" data-testid="intel-lib-selectall">{allSelected ? <CheckSquare size={14} className="text-[#1E5BA8]" /> : <Square size={14} />} Select all{selected.size ? ` (${selected.size})` : ''}</button>
          {selected.size > 0 && (<div className="flex items-center gap-2">
            <button onClick={() => setPicker({ kind: 'items', ids: [...selected] })} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1E5BA8] text-white text-[11px] font-bold" data-testid="intel-lib-move-selected"><FolderInput size={12} /> Move ({selected.size})</button>
            <button onClick={bulkDelete} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#DC2626] text-white text-[11px] font-bold" data-testid="intel-lib-delete-selected"><Trash2 size={12} /> Delete ({selected.size})</button>
          </div>)}
        </div>
      )}

      {shown.length === 0 && childFolders.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#64748B]" data-testid="intel-lib-empty">{currentFolder ? 'This folder is empty.' : 'Nothing saved yet.'} Save sources from the <b>Search</b> tab using “Save all to Library”.</div>
      ) : (
        <div className="space-y-3">
          {shown.map((it) => (
            <ResultCard key={it.item_id} r={it} brandId={it.brand_profile_id} variant="library" lib={{ selected: selected.has(it.item_id), onToggle: () => toggle(it.item_id), onMoveClick: () => setPicker({ kind: 'item', id: it.item_id }), onDelete: () => delOne(it.item_id), folderLabel: folderName(it.folder_id) }} />
          ))}
        </div>
      )}

      {picker && <FolderPickerModal folders={folders} title="Move to folder" onClose={() => setPicker(null)} onPick={doMove} onCreate={createFolderInline} />}
      {newFolder && <NameFolderModal parentName={newFolder.parentName} onClose={() => setNewFolder(null)} onSave={createFolder} />}
      {cal && <CalendarModal days={cal.days} sourcesUsed={cal.sourcesUsed} onClose={() => setCal(null)} onSend={sendDay} />}
    </div>
  );
}

/* ============================ SAVED SEARCHES ============================ */
function SavedSearchesView({ profiles }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(null);
  const load = useCallback(async () => { setLoading(true); try { const r = await api.get('/api/intelligence/saved-searches'); setRows(r.data.saved_searches || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { try { await api.delete(`/api/intelligence/saved-searches/${id}`); setRows((s) => s.filter((x) => x.saved_search_id !== id)); } catch { toast.error('Could not delete'); } };

  if (run) return <div><button onClick={() => setRun(null)} className="mb-3 text-sm font-bold text-[#1E5BA8]">← Back to search recipes</button><SearchView profiles={profiles} initial={{ ...run.filters, autorun: true }} /></div>;
  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;
  return (
    <div data-testid="intel-saved-searches">
      <div className="mb-4 p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] text-xs text-[#475569]" data-testid="intel-ss-explainer">
        <b className="text-[#0A0F1C]">Search Recipes</b> are search setups you saved (topic + filters) by clicking <b>“Save this search”</b> on the Search tab. They don't store articles — click <b>Run search</b> to run the same search again anytime. To get results automatically on a schedule with email alerts, use a <b>Watchlist</b> instead.
      </div>
      {rows.length === 0 ? <div className="py-16 text-center text-sm text-[#64748B]" data-testid="intel-ss-empty">No search recipes yet. Run a search on the Search tab and click “Save this search” to save the setup here for one-click re-runs.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((s) => (
            <div key={s.saved_search_id} className="bg-white rounded-2xl border border-[#E2E8F0] p-4" data-testid={`intel-ss-${s.saved_search_id}`}>
              <div className="flex items-start justify-between"><h3 className="font-bold text-sm text-[#0A0F1C]">{s.search_name}</h3><button onClick={() => del(s.saved_search_id)} className="text-[#94A3B8] hover:text-[#DC2626]"><Trash2 size={14} /></button></div>
              <p className="text-xs text-[#64748B] mt-1 line-clamp-2">{s.filters?.topic} · {s.filters?.mode}</p>
              <button onClick={() => setRun(s)} data-testid={`intel-ss-run-${s.saved_search_id}`} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold"><Search size={12} /> Run search</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ WATCHLISTS ============================ */
const SCHED_LABEL = { off: 'Manual', daily: 'Daily auto-run', weekly: 'Weekly auto-run' };
const hourLabel = (h) => { const hr = (h % 12) === 0 ? 12 : (h % 12); return `${hr}:00 ${h < 12 ? 'AM' : 'PM'}`; };
const cadenceLabel = (w) => {
  if (!w?.schedule || w.schedule === 'off') return 'Manual';
  const iv = w.interval_days || (w.schedule === 'weekly' ? 7 : 1);
  if (iv === 1) return 'Daily';
  if (iv === 7) return 'Weekly';
  if (iv === 14) return 'Every 2 weeks';
  return `Every ${iv} days`;
};
function WatchlistsView({ profiles, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState('');
  const load = useCallback(async () => { setLoading(true); try { const r = await api.get('/api/intelligence/watchlists'); setRows(r.data.watchlists || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { try { await api.delete(`/api/intelligence/watchlists/${id}`); setRows((s) => s.filter((x) => x.watchlist_id !== id)); } catch { toast.error('Could not delete'); } };
  const runNow = async (id) => {
    setRunning(id);
    try { const r = await api.post(`/api/intelligence/watchlists/${id}/run-now`); toast.success(`Briefing ready — ${r.data.briefing?.count || 0} sources found. Check the Briefings tab.`); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, 'Could not run watchlist')); } finally { setRunning(''); }
  };

  if (run) {
    const topic = (run.topics?.[0] || run.keywords?.[0] || run.watchlist_name);
    return <div><button onClick={() => setRun(null)} className="mb-3 text-sm font-bold text-[#1E5BA8]">← Back to watchlists</button>
      <SearchView profiles={profiles} initial={{ topic, brand_profile_id: run.brand_profile_id, location: run.location, mode: run.date_preference, content_goal: run.content_goal, autorun: true }} /></div>;
  }
  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;
  return (
    <div data-testid="intel-watchlists">
      <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-[#F8FAFC] border border-[#DBEAFE]" data-testid="intel-wl-explainer">
        <h3 className="text-sm font-bold text-[#0A0F1C] flex items-center gap-2 mb-1.5"><Eye size={16} className="text-[#1E5BA8]" /> What is a Watchlist?</h3>
        <p className="text-xs text-[#475569] leading-relaxed">
          A <b>Watchlist</b> is a topic you want to track over time. Instead of searching manually, you set it up once and it <b>runs the search automatically</b> on the schedule you choose — <b>daily, weekly, or a custom interval</b>. Every run collects fresh, credible sources into a <b>Briefing</b> that appears in the <b>Briefings tab</b>, where you can read it, expand on it, and send articles to your Post/Video/Flyer studios or save them to your Library.
        </p>
        <p className="text-xs text-[#475569] leading-relaxed mt-1.5">
          You can also have each Briefing <b>emailed to you</b> on your schedule, and hit <b>“Run now”</b> anytime to generate one instantly. <span className="text-[#1E5BA8] font-semibold">Set the topic, pick a schedule + email, and you're done.</span>
        </p>
      </div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => setModal({})} data-testid="intel-wl-new" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold"><Plus size={15} /> New watchlist</button>
        <p className="text-xs text-[#64748B] max-w-md">Each run produces a Briefing of fresh credible sources in the <b>Briefings</b> tab — optionally emailed to you.</p>
      </div>
      {rows.length === 0 ? <div className="py-16 text-center text-sm text-[#64748B]" data-testid="intel-wl-empty">No watchlists yet. Create one to track topics over time.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((w) => (
            <div key={w.watchlist_id} className="bg-white rounded-2xl border border-[#E2E8F0] p-4" data-testid={`intel-wl-${w.watchlist_id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-bold text-sm text-[#0A0F1C]">{w.watchlist_name}</h3>
                <div className="flex gap-1.5">
                  <button onClick={() => setModal(w)} className="text-[#94A3B8] hover:text-[#1E5BA8]" aria-label="Edit" data-testid={`intel-wl-edit-${w.watchlist_id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(w.watchlist_id)} className="text-[#94A3B8] hover:text-[#DC2626]" aria-label="Delete"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">{(w.topics || []).slice(0, 6).map((t, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1E5BA8] text-[10px] font-semibold">{t}</span>)}</div>
              <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${w.schedule && w.schedule !== 'off' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}><CalendarClock size={11} /> {cadenceLabel(w)}</span>
                {w.email_briefings && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 text-[10px] font-bold"><Mail size={11} /> Emailed</span>}
              </div>
              {w.next_run && w.schedule !== 'off' && <div className="text-[10px] text-[#94A3B8] mt-1.5 inline-flex items-center gap-1"><Clock size={10} /> Next run {String(w.next_run).slice(0, 10)}</div>}
              <div className="flex gap-1.5 mt-3">
                <button onClick={() => runNow(w.watchlist_id)} disabled={running === w.watchlist_id} data-testid={`intel-wl-runnow-${w.watchlist_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0F766E] text-white text-xs font-bold disabled:opacity-60">{running === w.watchlist_id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run now</button>
                <button onClick={() => setRun(w)} data-testid={`intel-wl-run-${w.watchlist_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold"><Search size={12} /> Open search</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && <WatchlistModal profiles={profiles} existing={modal.watchlist_id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); onChanged?.(); }} />}
    </div>
  );
}

function WatchlistModal({ profiles, existing, onClose, onSaved }) {
  const [name, setName] = useState(existing?.watchlist_name || '');
  const [topics, setTopics] = useState((existing?.topics || []).join(', '));
  const [brandId, setBrandId] = useState(existing?.brand_profile_id || '');
  const [location, setLocation] = useState(existing?.location || '');
  const [goal, setGoal] = useState(existing?.content_goal || '');
  const initCadence = () => {
    const sch = existing?.schedule;
    if (!sch || sch === 'off' || sch === 'manual') return 'manual';
    const iv = existing?.interval_days ?? (sch === 'weekly' ? 7 : 1);
    return ({ 1: 'daily', 2: 'd2', 3: 'd3', 4: 'd4', 7: 'weekly', 14: 'biweekly' })[iv] || 'custom';
  };
  const [cadence, setCadence] = useState(initCadence);
  const [customDays, setCustomDays] = useState(
    existing?.interval_days && ![1, 2, 3, 4, 7, 14].includes(existing.interval_days) ? existing.interval_days : 5);
  const [sendHour, setSendHour] = useState(existing?.send_hour ?? 8);
  const [email, setEmail] = useState(!!existing?.email_briefings);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error('Name your watchlist'); return; }
    setBusy(true);
    const tlist = topics.split(',').map((x) => x.trim()).filter(Boolean);
    const CMAP = { daily: 1, d2: 2, d3: 3, d4: 4, weekly: 7, biweekly: 14 };
    let schedule = 'off'; let interval_days = null;
    if (cadence !== 'manual') {
      schedule = 'on';
      interval_days = cadence === 'custom' ? Math.max(1, Math.min(Number(customDays) || 1, 90)) : CMAP[cadence];
    }
    const payload = {
      watchlist_name: name, brand_profile_id: brandId || null,
      topics: tlist, keywords: tlist, location, date_preference: 'recent_events',
      content_goal: goal, schedule, interval_days, send_hour: Number(sendHour),
      send_timezone: 'America/New_York', email_briefings: email,
    };
    try {
      if (existing?.watchlist_id) { await api.put(`/api/intelligence/watchlists/${existing.watchlist_id}`, payload); toast.success('Watchlist updated'); }
      else { await api.post('/api/intelligence/watchlists', payload); toast.success('Watchlist created'); }
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save watchlist')); } finally { setBusy(false); }
  };
  return (
    <Modal title={existing ? 'Edit Watchlist' : 'New Watchlist'} onClose={onClose} testid="intel-wl-modal" wide>
      <div className="space-y-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Watchlist name e.g. Entrepreneurship Watchlist" data-testid="intel-wl-name" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
        <textarea value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="Topics / keywords (comma separated) e.g. business closures, layoffs, side hustle" data-testid="intel-wl-topics" rows={2} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
          <option value="">No brand profile</option>
          {groupedProfileOptions(profiles)}
        </select>
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />

        <div className="space-y-3 p-3 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE]">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><CalendarClock size={12} /> Auto-run cadence</label>
            <select value={cadence} onChange={(e) => setCadence(e.target.value)} data-testid="intel-wl-schedule" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm">
              <option value="manual">Manual only (run when I click)</option>
              <option value="daily">Daily — every day</option>
              <option value="d2">Every other day (every 2 days)</option>
              <option value="d3">Every 3 days</option>
              <option value="d4">Every 4 days</option>
              <option value="weekly">Weekly — once a week</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="custom">Custom — every N days…</option>
            </select>
          </div>
          {cadence === 'custom' && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8]">Run every (days)</label>
              <input type="number" min={1} max={90} value={customDays} onChange={(e) => setCustomDays(e.target.value)} data-testid="intel-wl-custom-days" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm" />
            </div>
          )}
          {cadence !== 'manual' && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-[#1E5BA8] flex items-center gap-1"><Clock size={12} /> Send time (Eastern)</label>
              <select value={sendHour} onChange={(e) => setSendHour(e.target.value)} data-testid="intel-wl-sendhour" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#BFDBFE] text-sm">
                {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-semibold text-[#475569]">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} data-testid="intel-wl-email" /> <span className="inline-flex items-center gap-1"><Mail size={14} /> Email me each briefing</span>
          </label>
          <p className="text-[11px] text-[#94A3B8]">
            {cadence === 'manual'
              ? 'Runs only when you click “Run now”.'
              : <>Runs automatically at <b>{hourLabel(Number(sendHour))} Eastern</b>. You can still hit <b>“Run now”</b> on the watchlist for an instant briefing any time.</>}
          </p>
        </div>
        <button onClick={save} disabled={busy} data-testid="intel-wl-save" className="w-full px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : (existing ? 'Save changes' : 'Create watchlist')}</button>
      </div>
    </Modal>
  );
}

/* ============================ BRIEFINGS ============================ */
// Flatten folders into display order with depth (for indented pickers).
function folderTree(folders) {
  const byParent = {};
  (folders || []).forEach((f) => { const p = f.parent_folder_id || '__root__'; (byParent[p] = byParent[p] || []).push(f); });
  const out = [];
  const walk = (key, depth) => { (byParent[key] || []).forEach((f) => { out.push({ folder: f, depth }); walk(f.folder_id, depth + 1); }); };
  walk('__root__', 0);
  return out;
}

function FolderPickerModal({ folders, title, onClose, onPick, onCreate }) {
  const tree = folderTree(folders);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const f = await onCreate(name.trim()); if (f) onPick(f.folder_id); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={title || 'Move to folder'} onClose={onClose} testid="intel-folder-picker" wide>
      <div className="space-y-1" data-testid="intel-folder-picker-list">
        <button onClick={() => onPick(null)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F1F5F9] text-sm font-semibold flex items-center gap-2" data-testid="intel-folder-pick-unfiled">
          <Inbox size={15} className="text-[#94A3B8]" /> Unfiled (no folder)
        </button>
        {tree.length === 0 && !onCreate && <p className="text-xs text-[#94A3B8] px-3 py-3">No folders yet — close this and click <b>“+ New folder”</b> first.</p>}
        {tree.map(({ folder, depth }) => (
          <button key={folder.folder_id} onClick={() => onPick(folder.folder_id)} style={{ paddingLeft: 12 + depth * 20 }} className="w-full text-left pr-3 py-2 rounded-lg hover:bg-[#EFF6FF] text-sm flex items-center gap-2 text-[#0A0F1C]" data-testid={`intel-folder-pick-${folder.folder_id}`}>
            <FolderOpen size={15} className="text-[#1E5BA8] flex-shrink-0" /> {folder.name}
          </button>
        ))}
        {onCreate && (
          adding ? (
            <div className="flex items-center gap-1.5 px-2 pt-2">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} placeholder="New folder name" className="flex-1 px-2.5 py-1.5 rounded-lg border border-[#1E5BA8] text-sm" data-testid="intel-folder-pick-newname" />
              <button onClick={create} disabled={busy} className="px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold disabled:opacity-60" data-testid="intel-folder-pick-create">{busy ? '…' : 'Create'}</button>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} className="w-full text-left px-3 py-2 mt-1 rounded-lg border border-dashed border-[#CBD5E1] text-[#1E5BA8] text-sm font-bold flex items-center gap-2 hover:bg-[#EFF6FF]" data-testid="intel-folder-pick-addnew">
              <FolderPlus size={15} /> New folder here
            </button>
          )
        )}
      </div>
    </Modal>
  );
}

function NameFolderModal({ parentName, onClose, onSave }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error('Name your folder'); return; }
    setBusy(true); try { await onSave(name.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={parentName ? `New subfolder in “${parentName}”` : 'New folder'} onClose={onClose} testid="intel-newfolder-modal" wide>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} placeholder="Folder name e.g. Business, Social Media, Video Scripts" data-testid="intel-newfolder-name" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm mb-3" />
      <button onClick={save} disabled={busy} data-testid="intel-newfolder-save" className="w-full px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold disabled:opacity-60">{busy ? 'Creating…' : 'Create folder'}</button>
    </Modal>
  );
}

function ClipCard({ clip, checked, onToggle, onMove, onDelete, onSend }) {
  const r = clip.article || {};
  return (
    <div className={`flex items-start gap-2 border rounded-xl p-3 ${checked ? 'bg-blue-50/60 border-[#1E5BA8]' : 'border-[#E2E8F0]'}`} data-testid={`intel-clip-${clip.clip_id}`}>
      <button onClick={onToggle} className="mt-0.5 flex-shrink-0" aria-label="Select clip" data-testid={`intel-clip-check-${clip.clip_id}`}>
        {checked ? <CheckSquare size={15} className="text-[#1E5BA8]" /> : <Square size={15} className="text-[#CBD5E1]" />}
      </button>
      <div className="min-w-0 flex-1">
        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#1E5BA8] hover:underline line-clamp-1">{r.title}</a>
        <div className="text-[10px] text-[#94A3B8]">{r.publisher} · Opp {r.opportunity_score} · {r.credibility_rating} · from <b>{clip.watchlist_name}</b> · {String(clip.created_at).slice(0, 10)}</div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <button onClick={() => onSend(r, 'video')} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E2E8F0] text-[#475569] text-[11px] font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8]" data-testid={`intel-clip-video-${clip.clip_id}`}><Wand2 size={11} /> Video</button>
        <button onClick={() => onSend(r, 'social')} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E2E8F0] text-[#475569] text-[11px] font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8]" data-testid={`intel-clip-post-${clip.clip_id}`}><Send size={11} /> Post</button>
        <button onClick={onMove} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#E2E8F0] text-[#475569] hover:border-[#1E5BA8] hover:text-[#1E5BA8]" aria-label="Move clip" data-testid={`intel-clip-move-${clip.clip_id}`}><FolderInput size={13} /></button>
        <button onClick={onDelete} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#FCA5A5] text-[#DC2626] hover:bg-red-50" aria-label="Delete clip" data-testid={`intel-clip-delete-${clip.clip_id}`}><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

function BriefingsView({ onChanged }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState(undefined);
  const [tier, setTier] = useState('');
  const [folders, setFolders] = useState([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [clips, setClips] = useState([]);
  const [path, setPath] = useState([]); // breadcrumb of folder objects; [] = root inbox
  const [selected, setSelected] = useState({}); // { [briefing_id]: Set(result keys) }
  const [selClips, setSelClips] = useState(new Set());
  const [busy, setBusy] = useState('');
  const [picker, setPicker] = useState(null);   // move-to-folder config
  const [newFolder, setNewFolder] = useState(null); // { parent_folder_id, parentName } | null
  const [dragId, setDragId] = useState(null);    // folder being dragged

  const currentFolder = path.length ? path[path.length - 1] : null;
  const currentFolderId = currentFolder?.folder_id || null;

  const loadBriefings = useCallback(async () => {
    const r = await api.get('/api/intelligence/briefings');
    setRows(r.data.briefings || []); setRetention(r.data.retention_days); setTier(r.data.tier || '');
  }, []);
  const loadFolders = useCallback(async () => {
    const r = await api.get('/api/intelligence/briefing-folders');
    setFolders(r.data.folders || []); setUnfiledCount(r.data.unfiled_count || 0);
  }, []);
  const loadClips = useCallback(async (fid) => {
    const r = await api.get('/api/intelligence/clips', { params: { folder_id: fid == null ? 'unfiled' : fid } });
    setClips(r.data.clips || []);
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); try { await Promise.all([loadBriefings(), loadFolders()]); } finally { setLoading(false); } })();
  }, [loadBriefings, loadFolders]);
  useEffect(() => { loadClips(currentFolderId); setSelClips(new Set()); }, [currentFolderId, loadClips]);

  const refreshAll = async () => { await Promise.all([loadBriefings(), loadFolders(), loadClips(currentFolderId)]); onChanged?.(); };

  const markRead = async (b) => {
    if (b.read) return;
    try { await api.post(`/api/intelligence/briefings/${b.briefing_id}/read`); setRows((s) => s.map((x) => (x.briefing_id === b.briefing_id ? { ...x, read: true } : x))); onChanged?.(); } catch { /* ignore */ }
  };
  const sendTo = (r, where) => seedAndGo(navigate, r, where);
  const retentionLabel = retention ? `${retention} days` : 'Unlimited';
  const rkey = (r) => r.result_id || r.url;
  const folderName = (fid) => (fid ? (folders.find((f) => f.folder_id === fid)?.name || 'folder') : 'Unfiled');

  const toggle = (bid, key) => setSelected((s) => {
    const cur = new Set(s[bid] || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    return { ...s, [bid]: cur };
  });
  const toggleAll = (b) => setSelected((s) => {
    const allKeys = (b.results || []).map(rkey);
    const cur = s[b.briefing_id] || new Set();
    const allSelected = allKeys.length > 0 && allKeys.every((k) => cur.has(k));
    return { ...s, [b.briefing_id]: allSelected ? new Set() : new Set(allKeys) };
  });

  const removeResults = async (b, keys) => {
    const keySet = new Set(keys);
    const targets = (b.results || []).filter((r) => keySet.has(rkey(r)));
    if (!targets.length) return;
    const allGone = targets.length === (b.results || []).length;
    if (!window.confirm(allGone
      ? `Delete this entire "${b.watchlist_name}" briefing section (${targets.length} article${targets.length > 1 ? 's' : ''})?`
      : `Remove ${targets.length} selected article${targets.length > 1 ? 's' : ''} from this briefing?`)) return;
    setBusy(b.briefing_id);
    try {
      const res = await api.post(`/api/intelligence/briefings/${b.briefing_id}/remove-results`, {
        result_ids: targets.map((r) => r.result_id).filter(Boolean), urls: targets.map((r) => r.url).filter(Boolean),
      });
      if (res.data.deleted) { setRows((s) => s.filter((x) => x.briefing_id !== b.briefing_id)); toast.success('Briefing section deleted'); }
      else { setRows((s) => s.map((x) => (x.briefing_id === b.briefing_id ? { ...x, results: res.data.results, count: res.data.remaining } : x))); toast.success(`Removed ${targets.length} article${targets.length > 1 ? 's' : ''}`); }
      setSelected((s) => ({ ...s, [b.briefing_id]: new Set() })); onChanged?.();
    } catch (e) { toast.error(errMsg(e, 'Could not remove')); } finally { setBusy(''); }
  };

  const deleteSection = async (b) => {
    if (!window.confirm(`Delete the entire "${b.watchlist_name}" briefing section? This removes all ${(b.results || []).length} article(s).`)) return;
    setBusy(b.briefing_id);
    try { await api.delete(`/api/intelligence/briefings/${b.briefing_id}`); setRows((s) => s.filter((x) => x.briefing_id !== b.briefing_id)); toast.success('Briefing section deleted'); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, 'Could not delete section')); } finally { setBusy(''); }
  };

  // Move-to-folder picker. cfg: {kind:'articles', briefing, keys} | {kind:'clip', clipId} | {kind:'clips', clipIds}
  const doMove = async (folderId) => {
    const cfg = picker; setPicker(null); if (!cfg) return;
    try {
      if (cfg.kind === 'articles') {
        const b = cfg.briefing; const keySet = new Set(cfg.keys);
        const targets = (b.results || []).filter((r) => keySet.has(rkey(r)));
        const res = await api.post(`/api/intelligence/briefings/${b.briefing_id}/move-to-folder`, {
          folder_id: folderId, result_ids: targets.map((r) => r.result_id).filter(Boolean), urls: targets.map((r) => r.url).filter(Boolean),
        });
        toast.success(`Moved ${res.data.moved} article${res.data.moved > 1 ? 's' : ''} to ${folderName(folderId)}`);
        setSelected((s) => ({ ...s, [b.briefing_id]: new Set() }));
      } else if (cfg.kind === 'clip') {
        await api.post(`/api/intelligence/clips/${cfg.clipId}/move`, { folder_id: folderId });
        toast.success(`Moved to ${folderName(folderId)}`);
      } else if (cfg.kind === 'clips') {
        await api.post('/api/intelligence/clips/bulk-move', { clip_ids: cfg.clipIds, folder_id: folderId });
        toast.success(`Moved ${cfg.clipIds.length} to ${folderName(folderId)}`);
        setSelClips(new Set());
      }
      await refreshAll();
    } catch (e) { toast.error(errMsg(e, 'Could not move')); }
  };

  const createFolder = async (name) => {
    try { await api.post('/api/intelligence/briefing-folders', { name, parent_folder_id: newFolder?.parent_folder_id || null }); setNewFolder(null); toast.success('Folder created'); await loadFolders(); }
    catch (e) { toast.error(errMsg(e, 'Could not create folder')); }
  };
  const renameFolder = async (f) => {
    const name = window.prompt('Rename folder', f.name); if (!name || !name.trim()) return;
    try { await api.put(`/api/intelligence/briefing-folders/${f.folder_id}`, { name: name.trim() }); setPath((p) => p.map((x) => (x.folder_id === f.folder_id ? { ...x, name: name.trim() } : x))); await loadFolders(); }
    catch (e) { toast.error(errMsg(e, 'Could not rename')); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}"? Clips inside move back to Unfiled and subfolders move up — nothing is deleted.`)) return;
    try { await api.delete(`/api/intelligence/briefing-folders/${f.folder_id}`); toast.success('Folder deleted — contents kept'); setPath((p) => p.filter((x) => x.folder_id !== f.folder_id)); await Promise.all([loadFolders(), loadClips(currentFolderId)]); }
    catch (e) { toast.error(errMsg(e, 'Could not delete folder')); }
  };

  const toggleClip = (id) => setSelClips((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allClipsSelected = clips.length > 0 && clips.every((c) => selClips.has(c.clip_id));
  const toggleAllClips = () => setSelClips(allClipsSelected ? new Set() : new Set(clips.map((c) => c.clip_id)));
  const bulkDeleteClips = async () => {
    if (!selClips.size) return;
    if (!window.confirm(`Delete ${selClips.size} clip(s)? This cannot be undone.`)) return;
    try { await api.post('/api/intelligence/clips/bulk-delete', { clip_ids: [...selClips] }); toast.success(`Deleted ${selClips.size} clip(s)`); setSelClips(new Set()); await refreshAll(); }
    catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };
  const deleteOneClip = async (id) => {
    if (!window.confirm('Delete this clip?')) return;
    try { await api.delete(`/api/intelligence/clips/${id}`); await refreshAll(); } catch (e) { toast.error(errMsg(e, 'Could not delete')); }
  };

  const childFolders = folders.filter((f) => (f.parent_folder_id || null) === currentFolderId);

  const dropFolder = async (targetId) => {
    const ids = childFolders.map((f) => f.folder_id);
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const from = ids.indexOf(dragId); const to = ids.indexOf(targetId);
    setDragId(null);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setFolders((prev) => {
      const sib = prev.filter((f) => (f.parent_folder_id || null) === currentFolderId);
      const others = prev.filter((f) => (f.parent_folder_id || null) !== currentFolderId);
      const reordered = ids.map((id) => sib.find((f) => f.folder_id === id)).filter(Boolean);
      return [...others, ...reordered];
    });
    try { await api.post('/api/intelligence/briefing-folders/reorder', { ordered_ids: ids }); await loadFolders(); }
    catch (e) { toast.error('Could not reorder'); await loadFolders(); }
  };

  const sendAll = (where) => { if (clips.length) seedBatchAndGo(navigate, clips.map((c) => c.article || {}), where, currentFolder?.name || 'Folder'); };

  // Save briefing articles into the Saved Intelligence Library (closes Watchlists → Briefings → Library loop).
  const [libFolders, setLibFolders] = useState([]);
  const [libPicker, setLibPicker] = useState(null); // array of article objects pending save
  useEffect(() => { api.get('/api/intelligence/folders').then((r) => setLibFolders(r.data.folders || [])).catch(() => {}); }, []);
  const createLibFolder = async (name) => {
    try { const r = await api.post('/api/intelligence/folders', { name }); setLibFolders((s) => [...s, r.data.folder]); return r.data.folder; }
    catch (e) { toast.error('Could not create folder'); return null; }
  };
  const openSaveLib = (articles) => { if (!articles.length) { toast.error('Nothing to save'); return; } setLibPicker(articles); };
  const doSaveLib = async (folderId) => {
    const arts = libPicker || []; setLibPicker(null);
    try {
      const r = await api.post('/api/intelligence/library/bulk-save', { folder_id: folderId, results: arts });
      toast.success(`Saved ${r.data.saved} to Library${r.data.saved < arts.length ? ` · ${arts.length - r.data.saved} already saved` : ''}`);
    } catch (e) { toast.error(errMsg(e, 'Could not save to Library')); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-[#94A3B8]" /></div>;
  return (
    <div data-testid="intel-briefings">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3 p-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]" data-testid="intel-briefings-retention">
        <div className="text-xs text-[#475569] flex items-center gap-1.5"><History size={14} className="text-[#1E5BA8]" />
          <b>Briefing history</b> · {rows.length} kept
        </div>
        <div className="text-[11px] text-[#64748B]">
          Each briefing is one run of a <b>watchlist</b>. Retention on your <b className="capitalize">{tier || 'plan'}</b> plan: <b className="text-[#1E5BA8]">{retentionLabel}</b>
          {retention ? ' — older briefings are removed automatically.' : ' — full history kept.'}
        </div>
      </div>

      {/* Folder breadcrumb + create */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3" data-testid="intel-folder-bar">
        <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="intel-breadcrumb">
          <button onClick={() => setPath([])} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg font-bold ${currentFolder ? 'text-[#475569] hover:bg-[#F1F5F9]' : 'text-[#1E5BA8] bg-[#EFF6FF]'}`} data-testid="intel-breadcrumb-root">
            <Inbox size={14} /> Briefings
          </button>
          {path.map((f, i) => (
            <span key={f.folder_id} className="flex items-center gap-1">
              <ChevronRight size={13} className="text-[#CBD5E1]" />
              <button onClick={() => setPath((p) => p.slice(0, i + 1))} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg font-bold ${i === path.length - 1 ? 'text-[#1E5BA8] bg-[#EFF6FF]' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid={`intel-breadcrumb-${f.folder_id}`}>
                <FolderOpen size={14} /> {f.name}
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {currentFolder && (
            <>
              <button onClick={() => renameFolder(currentFolder)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#1E5BA8]" data-testid="intel-folder-rename"><Pencil size={12} /> Rename</button>
              <button onClick={() => deleteFolder(currentFolder)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#FCA5A5] text-[#DC2626] text-xs font-bold hover:bg-red-50" data-testid="intel-folder-delete"><Trash2 size={12} /> Delete folder</button>
            </>
          )}
          <button onClick={() => setNewFolder({ parent_folder_id: currentFolderId, parentName: currentFolder?.name || null })} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E5BA8] text-white text-xs font-bold" data-testid="intel-newfolder-btn">
            <FolderPlus size={14} /> {currentFolder ? 'New subfolder' : 'New folder'}
          </button>
        </div>
      </div>

      {/* Folder cards at this level */}
      {childFolders.length > 0 && (
        <>
          {childFolders.length > 1 && <div className="text-[10px] text-[#94A3B8] mb-1 ml-0.5">Tip: drag folders to reorder them.</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="intel-folder-grid">
            {childFolders.map((f) => {
              const subs = folders.filter((x) => x.parent_folder_id === f.folder_id).length;
              return (
                <div
                  key={f.folder_id}
                  draggable
                  onDragStart={() => setDragId(f.folder_id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropFolder(f.folder_id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => setPath((p) => [...p, f])}
                  className={`flex items-center gap-2 p-3 rounded-xl border bg-white hover:border-[#1E5BA8] hover:shadow-sm text-left transition-colors cursor-pointer ${dragId === f.folder_id ? 'opacity-40 border-dashed border-[#1E5BA8]' : 'border-[#E2E8F0]'}`}
                  data-testid={`intel-folder-card-${f.folder_id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0"><FolderOpen size={18} className="text-[#1E5BA8]" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-[#0A0F1C] truncate">{f.name}</div>
                    <div className="text-[11px] text-[#94A3B8]">{f.clip_count || 0} clip{(f.clip_count || 0) === 1 ? '' : 's'}{subs ? ` · ${subs} subfolder${subs === 1 ? '' : 's'}` : ''}</div>
                  </div>
                  <ChevronRight size={16} className="text-[#CBD5E1]" />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Send-all-to-studio bar (inside a folder with clips) */}
      {currentFolder != null && clips.length > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2 p-2.5 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="intel-folder-sendall">
          <span className="text-[11px] font-bold text-[#15803D] flex items-center gap-1.5"><Sparkles size={13} /> Turn all {clips.length} clip{clips.length > 1 ? 's' : ''} in this folder into content</span>
          <div className="flex items-center gap-2">
            <button onClick={() => sendAll('post')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#0F766E] text-white text-[11px] font-bold hover:bg-[#0d655e]" data-testid="intel-folder-sendall-post"><Send size={12} /> Send all → Post Studio</button>
            <button onClick={() => sendAll('video')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#7C3AED] text-white text-[11px] font-bold hover:bg-[#6D28D9]" data-testid="intel-folder-sendall-video"><Wand2 size={12} /> Send all → Video Studio</button>
          </div>
        </div>
      )}

      {/* Clips toolbar (bulk move/delete) — visible when there are clips in the current view */}
      {clips.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-2 p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" onClick={(e) => e.stopPropagation()} data-testid="intel-clips-toolbar">
          <button onClick={toggleAllClips} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#475569] hover:text-[#1E5BA8]" data-testid="intel-clips-selectall">
            {allClipsSelected ? <CheckSquare size={14} className="text-[#1E5BA8]" /> : <Square size={14} />}
            Select all clips{selClips.size ? ` (${selClips.size})` : ''}
          </button>
          {selClips.size > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setPicker({ kind: 'clips', clipIds: [...selClips] })} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1E5BA8] text-white text-[11px] font-bold hover:bg-[#16467f]" data-testid="intel-clips-move"><FolderInput size={12} /> Move to folder ({selClips.size})</button>
              <button onClick={bulkDeleteClips} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#DC2626] text-white text-[11px] font-bold hover:bg-red-700" data-testid="intel-clips-delete"><Trash2 size={12} /> Delete ({selClips.size})</button>
            </div>
          )}
        </div>
      )}

      {/* Clips list (folder contents, or unfiled clips at root) */}
      {clips.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="intel-clips-list">
          {currentFolder == null && <div className="text-[11px] font-bold uppercase tracking-wide text-[#94A3B8] flex items-center gap-1.5"><Inbox size={12} /> Unfiled clips</div>}
          {clips.map((c) => (
            <ClipCard key={c.clip_id} clip={c} checked={selClips.has(c.clip_id)} onToggle={() => toggleClip(c.clip_id)} onMove={() => setPicker({ kind: 'clip', clipId: c.clip_id })} onDelete={() => deleteOneClip(c.clip_id)} onSend={sendTo} />
          ))}
        </div>
      )}

      {/* Briefing sections — only at the root inbox */}
      {currentFolder == null && (
        rows.length === 0 && clips.length === 0 && childFolders.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#64748B]" data-testid="intel-briefings-empty">No briefings yet. Set a watchlist to auto-run (daily/weekly) or hit “Run now” on a watchlist to generate one.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((b) => {
              const sel = selected[b.briefing_id] || new Set();
              const allKeys = (b.results || []).map(rkey);
              const allSelected = allKeys.length > 0 && allKeys.every((k) => sel.has(k));
              const selCount = allKeys.filter((k) => sel.has(k)).length;
              return (
              <div key={b.briefing_id} onClick={() => markRead(b)} className={`bg-white rounded-2xl border p-4 ${b.read ? 'border-[#E2E8F0]' : 'border-[#1E5BA8] ring-1 ring-[#1E5BA8]/30'}`} data-testid={`intel-briefing-${b.briefing_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-sm text-[#0A0F1C] flex items-center gap-2">
                      {!b.read && <span className="w-2 h-2 rounded-full bg-[#1E5BA8]" />}{b.watchlist_name}
                      {b.source === 'search' && <span className="text-[9px] font-bold uppercase tracking-wide text-[#1E5BA8] bg-[#EFF6FF] border border-[#DBEAFE] px-1.5 py-0.5 rounded-full" data-testid={`intel-search-badge-${b.briefing_id}`}>Search</span>}
                    </h3>
                    <div className="text-[11px] text-[#64748B] mt-0.5">{b.topic} · {b.count} sources · {String(b.created_at).slice(0, 16).replace('T', ' ')}</div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); openSaveLib(b.results || []); }} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#1E5BA8] text-[#1E5BA8] text-[11px] font-bold hover:bg-[#EFF6FF]" data-testid={`intel-briefing-savelib-section-${b.briefing_id}`}>
                      <Bookmark size={11} /> Save all → Library
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteSection(b); }} disabled={busy === b.briefing_id} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#FCA5A5] text-[#DC2626] text-[11px] font-bold hover:bg-red-50 disabled:opacity-50" data-testid={`intel-briefing-delete-section-${b.briefing_id}`}>
                      <Trash2 size={11} /> Delete section
                    </button>
                  </div>
                </div>

                {(b.results || []).length > 0 && (
                  <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-[#F1F5F9]" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => toggleAll(b)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#475569] hover:text-[#1E5BA8]" data-testid={`intel-briefing-selectall-${b.briefing_id}`}>
                      {allSelected ? <CheckSquare size={14} className="text-[#1E5BA8]" /> : <Square size={14} />}
                      Select all{selCount > 0 ? ` (${selCount} selected)` : ''}
                    </button>
                    {selCount > 0 && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setPicker({ kind: 'articles', briefing: b, keys: [...sel] })} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1E5BA8] text-white text-[11px] font-bold hover:bg-[#16467f]" data-testid={`intel-briefing-move-selected-${b.briefing_id}`}><FolderInput size={12} /> Move to folder ({selCount})</button>
                        <button onClick={() => openSaveLib((b.results || []).filter((r) => sel.has(rkey(r))))} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#1E5BA8] text-[#1E5BA8] text-[11px] font-bold hover:bg-[#EFF6FF]" data-testid={`intel-briefing-savelib-selected-${b.briefing_id}`}><Bookmark size={12} /> Save ({selCount}) → Library</button>
                        <button onClick={() => removeResults(b, [...sel])} disabled={busy === b.briefing_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#DC2626] text-white text-[11px] font-bold hover:bg-red-700 disabled:opacity-50" data-testid={`intel-briefing-delete-selected-${b.briefing_id}`}>
                          {busy === b.briefing_id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} Delete ({selCount})
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className={`space-y-2 mt-2 ${(b.results || []).length > 8 ? 'max-h-[480px] overflow-y-auto pr-1' : ''}`} data-testid={`intel-briefing-scroll-${b.briefing_id}`}>
                  {(b.results || []).map((r, i) => {
                    const key = rkey(r); const checked = sel.has(key);
                    return (
                    <div key={key || i} className={`flex items-start gap-2 border-t border-[#F1F5F9] pt-2 ${checked ? 'bg-blue-50/50 -mx-1 px-1 rounded' : ''}`} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggle(b.briefing_id, key)} className="mt-0.5 flex-shrink-0" aria-label="Select article" data-testid={`intel-briefing-check-${b.briefing_id}-${i}`}>
                        {checked ? <CheckSquare size={15} className="text-[#1E5BA8]" /> : <Square size={15} className="text-[#CBD5E1]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#1E5BA8] hover:underline line-clamp-1">{r.title}</a>
                        <div className="text-[10px] text-[#94A3B8]">{r.publisher} · Opp {r.opportunity_score} · {r.credibility_rating}</div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => sendTo(r, 'video')} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E2E8F0] text-[#475569] text-[11px] font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8]" data-testid={`intel-briefing-video-${b.briefing_id}`}><Wand2 size={11} /> Video Studio</button>
                        <button onClick={() => sendTo(r, 'social')} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E2E8F0] text-[#475569] text-[11px] font-bold hover:border-[#1E5BA8] hover:text-[#1E5BA8]" data-testid={`intel-briefing-post-${b.briefing_id}`}><Send size={11} /> Post Studio</button>
                        <button onClick={() => openSaveLib([r])} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#1E5BA8] text-[#1E5BA8] hover:bg-[#EFF6FF]" aria-label="Save to Library" data-testid={`intel-briefing-savelib-item-${b.briefing_id}-${i}`}><Bookmark size={13} /></button>
                        <button onClick={() => setPicker({ kind: 'articles', briefing: b, keys: [key] })} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#E2E8F0] text-[#475569] hover:border-[#1E5BA8] hover:text-[#1E5BA8]" aria-label="Move to folder" data-testid={`intel-briefing-move-item-${b.briefing_id}-${i}`}><FolderInput size={13} /></button>
                        <button onClick={() => removeResults(b, [key])} disabled={busy === b.briefing_id} className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#FCA5A5] text-[#DC2626] hover:bg-red-50 disabled:opacity-50" aria-label="Delete article" data-testid={`intel-briefing-delete-item-${b.briefing_id}-${i}`}><Trash2 size={12} /></button>
                      </div>
                    </div>
                    );
                  })}
                  {(!b.results || b.results.length === 0) && <p className="text-xs text-[#94A3B8]">No fresh sources found in this run.</p>}
                </div>
              </div>
              );
            })}
          </div>
        )
      )}

      {/* Empty folder hint */}
      {currentFolder != null && clips.length === 0 && childFolders.length === 0 && (
        <div className="py-14 text-center text-sm text-[#64748B]" data-testid="intel-folder-empty">
          This folder is empty. Move briefing articles here from the <button onClick={() => setPath([])} className="text-[#1E5BA8] font-bold underline">Briefings inbox</button>, or create a subfolder.
        </div>
      )}

      {picker && <FolderPickerModal folders={folders} title={picker.kind === 'articles' ? 'Move article(s) to folder' : 'Move clip(s) to folder'} onClose={() => setPicker(null)} onPick={doMove} />}
      {libPicker && <FolderPickerModal folders={libFolders} title={`Save ${libPicker.length} to Library`} onClose={() => setLibPicker(null)} onPick={doSaveLib} onCreate={createLibFolder} />}
      {newFolder && <NameFolderModal parentName={newFolder.parentName} onClose={() => setNewFolder(null)} onSave={createFolder} />}
    </div>
  );
}
